import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteTriggerProfile } from '@/lib/api/generated/api';

import { toTriggerCreateError } from '../errors';
import { triggerKeys } from '../queryKeys';

/**
 * Soft-delete a Trigger Profile of any driver type (spec 074, US2,
 * FR-005/FR-006). Not reversible: a second call on the same id refuses with
 * `TriggerProfileDeletedError` (mapped by `toTriggerCreateError`, the same
 * mapper every other trigger-profile mutation already uses).
 *
 * Invalidates the list (the deleted profile drops out immediately) and the
 * detail read (it still resolves, now with `deleted_at` set — a page keeping
 * it open, e.g. a run-history view, sees that on its next read rather than a
 * stale "still active" row).
 */
export function useDeleteTriggerProfile(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (triggerProfileId: string): Promise<void> => {
      const response = await deleteTriggerProfile(
        workspaceId,
        triggerProfileId,
      );
      if (response.status === 204) return;
      throw toTriggerCreateError(response.status, response.data);
    },
    onSuccess: (_void, triggerProfileId) => {
      void queryClient.invalidateQueries({
        queryKey: triggerKeys.list(workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: triggerKeys.detail(workspaceId, triggerProfileId),
      });
    },
  });
}
